import { useState } from 'react';
import { Sparkles, RefreshCw, Copy, Plus, Check, AlertTriangle } from 'lucide-react';
import { chAiService } from '../services/aiService';
import { PLATFORM_LABELS } from '../types';
import type { ChPostPlatform } from '../types';

interface Props {
  projectId: string;
  postId: string;
  title: string;
  caption: string;
  currentPlatform: ChPostPlatform;
  onCreatePost?: (platform: ChPostPlatform, caption: string) => void;
}

export function AiAdaptPlatformsPanel({ projectId, postId, title, caption, currentPlatform, onCreatePost }: Props) {
  const [generating, setGenerating] = useState(false);
  const [adaptations, setAdaptations] = useState<Record<string, string> | null>(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState<string | null>(null);
  const [noToneProfile, setNoToneProfile] = useState(false);

  async function handleAdapt() {
    if (!caption.trim()) {
      setError('O post precisa de uma caption para adaptar.');
      return;
    }

    setGenerating(true);
    setError('');

    try {
      const res = await chAiService.adaptPlatforms({
        project_id: projectId,
        post_id: postId,
        caption,
        current_platform: currentPlatform,
        title,
      });

      if (!res.success) {
        setError(res.error || 'Erro ao adaptar');
        return;
      }

      setNoToneProfile(!res.tone_profile_set);
      if (res.data?.adaptations) {
        setAdaptations(res.data.adaptations);
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao adaptar');
    } finally {
      setGenerating(false);
    }
  }

  function handleCopy(text: string, platform: string) {
    navigator.clipboard.writeText(text);
    setCopied(platform);
    setTimeout(() => setCopied(null), 2000);
  }

  if (!adaptations && !generating && !error) {
    return (
      <button
        type="button"
        onClick={handleAdapt}
        disabled={!caption}
        className="ch-btn ch-btn-ghost ch-btn-sm inline-flex items-center gap-1.5"
      >
        <Sparkles className="w-3.5 h-3.5" />
        Adaptar a outras plataformas
      </button>
    );
  }

  return (
    <div className="ch-card space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-[var(--ch-ink)] flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-500" />
          Adaptações por Plataforma
        </h3>
        <button
          type="button"
          onClick={handleAdapt}
          disabled={generating}
          className="text-xs text-gray-400 hover:text-[var(--ch-ink)] flex items-center gap-1"
        >
          <RefreshCw className={`w-3 h-3 ${generating ? 'animate-spin' : ''}`} />
          Regenerar
        </button>
      </div>

      {noToneProfile && (
        <div className="flex items-start gap-2 p-2 rounded-lg bg-amber-50 border border-amber-100 text-xs text-amber-700">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>Projeto sem perfil de tom definido.</span>
        </div>
      )}

      {error && (
        <div className="p-2 rounded-lg bg-red-50 text-red-700 text-xs">{error}</div>
      )}

      {generating && (
        <div className="flex items-center gap-2 p-4 rounded-xl bg-gray-50">
          <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />
          <span className="text-sm text-gray-500">A gerar adaptações...</span>
        </div>
      )}

      {adaptations && (
        <div className="space-y-3">
          {Object.entries(adaptations).map(([platform, text]) => (
            <div key={platform} className="p-3 rounded-xl border border-gray-100 bg-gray-50/50 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-[var(--ch-ink)]">
                  {PLATFORM_LABELS[platform as ChPostPlatform] || platform}
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => handleCopy(text, platform)}
                    className="p-1 rounded hover:bg-gray-200 transition-colors"
                    title="Copiar"
                  >
                    {copied === platform ? (
                      <Check className="w-3.5 h-3.5 text-green-600" />
                    ) : (
                      <Copy className="w-3.5 h-3.5 text-gray-400" />
                    )}
                  </button>
                  {onCreatePost && (
                    <button
                      type="button"
                      onClick={() => onCreatePost(platform as ChPostPlatform, text)}
                      className="p-1 rounded hover:bg-gray-200 transition-colors"
                      title="Criar post com esta versão"
                    >
                      <Plus className="w-3.5 h-3.5 text-gray-400" />
                    </button>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed whitespace-pre-wrap">{text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
